import { Box } from '@mui/material';
import useWidth from '../../../../../../components/Hooks/useWidth';
import { useMediaApplicationContext } from '../../../../../../components/MediaApplicationContext';
import { MediaFormatEnum } from '../../../../../general-settings/enums/mediaFormat.enum';
import { ProductMedia } from '../../../../ProductTypes';
import useThumbStripScroll from '../../hooks/useThumbStripScroll';
import { getThumbPlaceholdersCount } from '../../utils/productThumbPlaceholders.utils';
import ProductGalleryNavButtons from '../ProductGalleryNavButtons/ProductGalleryNavButtons';
import ThumbMediaV2 from './ThumbMediaV2';
import { useGalleryThumbStripStyles } from './galleryThumbStrip.styles';

interface GalleryThumbStripProps {
  medias: ProductMedia[];
  mediaFormat: MediaFormatEnum;
  thumbIndexes: number[];
  thumbWidth: number;
  thumbHeight: number;
  thumbRows: number;
  thumbColumns: number;
  maxThumbs: number;
  isScrollable: boolean;
  selectedIndex: number;
  onZoom: (media: ProductMedia, focusPercentToScroll?: number) => void;
  onSelect: (index: number) => void;
}

const GalleryThumbStrip = ({
  medias,
  mediaFormat,
  thumbIndexes,
  thumbWidth,
  thumbHeight,
  thumbRows,
  thumbColumns,
  maxThumbs,
  isScrollable,
  selectedIndex,
  onZoom,
  onSelect,
}: GalleryThumbStripProps) => {
  const { classes, cx } = useGalleryThumbStripStyles({
    mediaFormat,
    thumbWidth,
    thumbHeight,
    thumbRows,
    thumbColumns,
    isScrollable,
  });
  const { mediumDownWidth } = useWidth();
  const mediaContext = useMediaApplicationContext();
  const { stripRef, scrollToPreviousThumb, scrollToNextThumb } = useThumbStripScroll();

  const placeholdersCount = getThumbPlaceholdersCount({
    maxThumbs,
    thumbsCount: thumbIndexes.length,
  });

  const getThumbUrl = (media: ProductMedia) => {
    if (mediumDownWidth || media.type !== 'IMAGE') {
      return media.url;
    }

    return mediaContext.getMediaFromSize(media.url, 'sm');
  };

  if (!thumbIndexes.length && !placeholdersCount) {
    return null;
  }

  return (
    <Box className={classes.thumbStripWrapper}>
      <Box ref={stripRef} className={classes.thumbStrip}>
        {thumbIndexes.map((mediaIndex) => {
          const media = medias[mediaIndex];

          if (!media) {
            return null;
          }

          return (
            <Box
              key={media.id ?? media.url}
              className={cx(classes.thumb, {
                [classes.thumbSelected]: mediaIndex === selectedIndex,
              })}
              onClick={() => onSelect(mediaIndex)}
            >
              <ThumbMediaV2 media={media} onZoom={onZoom} mediaUrl={getThumbUrl(media)} />
            </Box>
          );
        })}

        {Array.from({ length: placeholdersCount }, (_, index) => (
          <Box
            key={`thumb-placeholder-${index}`}
            aria-hidden
            className={cx(classes.thumb, classes.thumbPlaceholder)}
          />
        ))}
      </Box>

      <ProductGalleryNavButtons
        className={classes.thumbStripNav}
        onPrevious={scrollToPreviousThumb}
        onNext={scrollToNextThumb}
      />
    </Box>
  );
};

export default GalleryThumbStrip;
